/**
 * Metric definitions and the three orderings the report depends on —
 * Document 6 §10.5, §12.3, §13.1.
 */

export const METRIC_KEYS = [
  'weight',
  'bmi',
  'waist',
  'hip',
  'chest',
  'rightArm',
  'leftArm',
  'rightThigh',
  'leftThigh',
  'bodyFat',
  'muscle',
] as const;

export type MetricKey = (typeof METRIC_KEYS)[number];

export interface MetricDefinition {
  key: MetricKey;
  /** Turkish label, frozen into the snapshot at generation time. */
  label: string;
  /** 'kg' | 'cm' | '%' | '' — muscle is resolved at runtime from muscle_unit. */
  unit: string;
  decimals: number;
  /** Column in `measurements`. BMI is derived and has no column. */
  column: string | null;
}

/**
 * Fixed anatomical order for the physical progress table (Doc 6 §10.5).
 * This is the render order; it is not a priority order.
 */
export const METRICS: Record<MetricKey, MetricDefinition> = {
  weight: { key: 'weight', label: 'Kilo', unit: 'kg', decimals: 1, column: 'weight_kg' },
  bmi: { key: 'bmi', label: 'BMI', unit: '', decimals: 1, column: null },
  waist: { key: 'waist', label: 'Bel', unit: 'cm', decimals: 1, column: 'waist_cm' },
  hip: { key: 'hip', label: 'Kalça', unit: 'cm', decimals: 1, column: 'hip_cm' },
  chest: { key: 'chest', label: 'Göğüs', unit: 'cm', decimals: 1, column: 'chest_cm' },
  rightArm: {
    key: 'rightArm',
    label: 'Sağ Kol',
    unit: 'cm',
    decimals: 1,
    column: 'right_arm_cm',
  },
  leftArm: {
    key: 'leftArm',
    label: 'Sol Kol',
    unit: 'cm',
    decimals: 1,
    column: 'left_arm_cm',
  },
  rightThigh: {
    key: 'rightThigh',
    label: 'Sağ Bacak',
    unit: 'cm',
    decimals: 1,
    column: 'right_thigh_cm',
  },
  leftThigh: {
    key: 'leftThigh',
    label: 'Sol Bacak',
    unit: 'cm',
    decimals: 1,
    column: 'left_thigh_cm',
  },
  bodyFat: {
    key: 'bodyFat',
    label: 'Yağ Oranı',
    unit: '%',
    decimals: 1,
    column: 'body_fat_pct',
  },
  muscle: {
    key: 'muscle',
    label: 'Kas Oranı',
    unit: '%',
    decimals: 1,
    column: 'muscle_value',
  },
};

/** Render order for the physical progress table — Doc 6 §10.5. */
export const METRIC_TABLE_ORDER: readonly MetricKey[] = METRIC_KEYS;

/**
 * Chart order — Doc 6 §13.1.
 *
 * Since 6 September 2026 this is the order charts are *drawn* in, not a
 * priority for choosing between them: every metric in the physical table gets
 * a chart, and it reads in the same sequence as the table it sits under.
 *
 * BMI is charted like everything else. It tracks weight, so its line has the
 * same shape as the weight chart at a different scale — the product owner
 * asked for a picture under every row of the table and that is what a row
 * without one looks like from the reader's side.
 */
export const CHART_PRIORITY: readonly MetricKey[] = [
  'weight',
  'waist',
  'hip',
  'bodyFat',
  'muscle',
  'chest',
  'rightThigh',
  'leftThigh',
  'rightArm',
  'leftArm',
];

/**
 * Every metric in the table may be charted. It was three, chosen by priority,
 * until the product owner pointed out the obvious: a row in the table with no
 * picture under it looks like data the report forgot.
 */
export const MAX_CHARTS = METRIC_KEYS.length;
/**
 * Two, since 6 September 2026. It used to be three, on the reasoning that two
 * points is a line between two dots implying a trend that was never measured.
 * That reasoning is still right, and it is why the connector between exactly
 * two points is drawn dashed (§13.3) — but once the period began bounding the
 * data, a three-point minimum meant an ordinary monthly report had no chart at
 * all, which is a worse answer than an honest sparse one.
 */
export const MIN_CHART_POINTS = 2;
export const MAX_CHART_POINTS = 8;

export function isMetricKey(value: unknown): value is MetricKey {
  return typeof value === 'string' && (METRIC_KEYS as readonly string[]).includes(value);
}

/** Muscle is stored as either a percentage or kilograms — Schema §9. */
export type MuscleUnit = 'percent' | 'kg';

export function muscleUnitSymbol(unit: MuscleUnit): string {
  return unit === 'percent' ? '%' : 'kg';
}

export function muscleLabel(unit: MuscleUnit): string {
  return unit === 'percent' ? 'Kas Oranı' : 'Kas Kütlesi';
}

/** Instructor assessment categories, in report render order — Doc 6 §10.10. */
export const ASSESSMENT_KEYS = [
  'coreControl',
  'balance',
  'flexibility',
  'lowerBodyStrength',
  'upperBodyControl',
] as const;

export type AssessmentKey = (typeof ASSESSMENT_KEYS)[number];

export const ASSESSMENT_LABELS: Record<AssessmentKey, string> = {
  coreControl: 'Core Kontrolü',
  balance: 'Denge',
  flexibility: 'Esneklik',
  lowerBodyStrength: 'Alt Vücut Kuvveti',
  upperBodyControl: 'Üst Vücut Kontrolü',
};

export const ASSESSMENT_MIN_SCORE = 1;
export const ASSESSMENT_MAX_SCORE = 5;

/** Progress photo categories — Schema §10. */
export const PHOTO_CATEGORIES = [
  'front',
  'side',
  'back',
  'arm',
  'leg',
  'waist',
  'hip',
  'custom',
] as const;

export type PhotoCategory = (typeof PHOTO_CATEGORIES)[number];

export function isPhotoCategory(value: unknown): value is PhotoCategory {
  return (
    typeof value === 'string' && (PHOTO_CATEGORIES as readonly string[]).includes(value)
  );
}

export const PHOTO_CATEGORY_LABELS: Record<PhotoCategory, string> = {
  front: 'Ön',
  side: 'Yan',
  back: 'Arka',
  arm: 'Kol',
  leg: 'Bacak',
  waist: 'Bel',
  hip: 'Kalça',
  custom: 'Diğer',
};

/** Page 3 — full body comparison, in fixed order (Doc 6 §10.6). */
export const FULL_BODY_CATEGORIES: readonly PhotoCategory[] = ['front', 'side', 'back'];

/** A posture photo's angle — always exactly these three (Schema §posture). */
export const POSTURE_ANGLES = ['front', 'side', 'back'] as const;
export type PostureAngle = (typeof POSTURE_ANGLES)[number];

/** Report wizard / PDF comparison cap — UX/UI §9.11, §10.9. */
export const POSTURE_COMPARISON_MAX = 3;

export function isPostureAngle(value: unknown): value is PostureAngle {
  return (
    typeof value === 'string' && (POSTURE_ANGLES as readonly string[]).includes(value)
  );
}

/** Page 4 — regional comparison, in fixed order (Doc 6 §10.7). */
export const REGIONAL_CATEGORIES: readonly PhotoCategory[] = [
  'waist',
  'hip',
  'leg',
  'arm',
  'custom',
];
